export const supportedCurrencies = ["CAD", "USD"] as const;
export type SupportedCurrency = (typeof supportedCurrencies)[number];

export function isSupportedCurrency(value: string): value is SupportedCurrency {
  return supportedCurrencies.includes(value as SupportedCurrency);
}

export function supportedCurrencyOrDefault(value: string | undefined, fallback: SupportedCurrency = "CAD"): SupportedCurrency {
  return value && isSupportedCurrency(value) ? value : fallback;
}

export function parseMoneyToMinor(input: string, fractionDigits = 2): number {
  const normalized = input.trim().replace(/[$,\s]/g, "").replace(/^(-?)\.(?=\d)/, "$10.");
  if (!/^-?\d+(?:\.\d{0,2})?$/.test(normalized)) {
    throw new Error("Enter a valid amount with no more than two decimal places.");
  }
  const sign = normalized.startsWith("-") ? -1 : 1;
  const unsigned = normalized.replace(/^-/, "");
  const [whole = "0", fraction = ""] = unsigned.split(".");
  const scale = 10 ** fractionDigits;
  const paddedFraction = fraction.padEnd(fractionDigits, "0").slice(0, fractionDigits);
  const minor = Number.parseInt(whole, 10) * scale + Number.parseInt(paddedFraction || "0", 10);
  if (!Number.isSafeInteger(minor)) throw new Error("Amount is too large.");
  return sign * minor;
}

export type CashDirection = "expense" | "income";

export function parsePositiveMoneyToMinor(input: string, fractionDigits = 2): number {
  const amountMinor = parseMoneyToMinor(input, fractionDigits);
  if (amountMinor <= 0) throw new Error("Enter an amount greater than zero.");
  return amountMinor;
}

export function applyCashDirection(amountMinor: number, direction: CashDirection): number {
  const positiveAmountMinor = Math.abs(amountMinor);
  return direction === "expense" ? -positiveAmountMinor : positiveAmountMinor;
}

export function formatMoney(amountMinor: number, currency = "CAD", locale = "en-CA"): string {
  if (!Number.isSafeInteger(amountMinor)) throw new Error("Money must use integer minor units.");
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
  }).format(amountMinor / 100);
}

export function assertMinorUnits(value: number): void {
  if (!Number.isSafeInteger(value)) throw new Error("Money values must be safe integers in minor units.");
}
